import { useLockedBody, useResponsive } from 'front-commons/hooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { HEADER_HEIGHT_CSS_VAR } from '../../constants';
import type { HeaderContextProps } from '../../context/interfaces';
import type { UseHeaderStateProps } from './interfaces';

/**
 * Owns Header composition state and its global height publication.
 *
 * ---
 * 🧠 **Context**
 *
 * - Keeps mobile category openness, selector snapshots and top-row visibility under one Header instance.
 * - Publishes --header-height and releases it during effect cleanup; shared locking remains delegated to front-commons.
 * - The dynamic pharmacy-availability memo currently depends on type only.
 *
 * ---
 * @example
 * ```ts
 * function useCommerceHeaderState(props: UseHeaderStateProps) {
 *   const state = useHeaderState({ ...props, type: 'home', stickyTopRow: true });
 *   return { ...state, openCategories: () => state.setShowCategories(true) };
 * }
 * ```
 *
 * ---
 * @function useHeaderState
 * @param {UseHeaderStateProps} props - Header mode, sticky behavior, customer and pharmacy sources.
 * @returns {ReturnType<typeof useHeaderState>} Composition decisions, shared local state, DOM ref and Router location.
 */
export function useHeaderState({ type, stickyTopRow, posStore, customerStore }: UseHeaderStateProps) {
	const [showCategories, setShowCategories] = useState(false);

	const location = useLocation();
	const responsive = useResponsive();

	const headerRef = useRef<HTMLElement>(null);

	const [posSelector, setPosSelector] = useState<HeaderContextProps['pharmacy']['posSelector']>({});
	const [isTopRowVisible, setIsTopRowVisible] = useState(true);

	useLockedBody({ state: !!posSelector?.expanded });

	const handleTopRowVisibilityChange = useCallback((visible: boolean) => {
		setIsTopRowVisible(visible);
	}, []);

	const showInDynamicMode = useCallback(
		(condition: boolean) => {
			return type === 'dynamic' && condition;
		},
		[type],
	);

	const isHomeOrDynamicWithPharmacy = useMemo(
		() => type === 'home' || showInDynamicMode(!!posStore.selectedPos?.id),
		[type],
	);

	const isValidCustomer = !!customerStore.token && !!customerStore.data.name;

	const showBrandSectionMenu = type !== 'not_logged' || showInDynamicMode(true);

	const showBasketButton =
		isHomeOrDynamicWithPharmacy &&
		location.pathname !== '/revisar-pedido' &&
		location.pathname !== '/otimizacao-de-pedido';

	const isHelpPage = location.pathname === '/perguntas-frequentes';

	useEffect(() => {
		if (!stickyTopRow) {
			if (headerRef.current) {
				const { height } = headerRef.current.getBoundingClientRect();

				document.documentElement.style.setProperty(HEADER_HEIGHT_CSS_VAR, `${height}px`);
			}

			return () => {
				document.documentElement.style.removeProperty(HEADER_HEIGHT_CSS_VAR);
			};
		}

		if (isTopRowVisible) {
			document.documentElement.style.setProperty(
				HEADER_HEIGHT_CSS_VAR,
				'calc(var(--toprow-height, 0px) + var(--bottomrow-height, 0px))',
			);
		} else {
			document.documentElement.style.setProperty(HEADER_HEIGHT_CSS_VAR, 'var(--bottomrow-height, 0px)');
		}

		return () => {
			document.documentElement.style.removeProperty(HEADER_HEIGHT_CSS_VAR);
		};
	}, [isTopRowVisible, stickyTopRow, headerRef, responsive]);

	return {
		showCategories,
		setShowCategories,
		location,
		headerRef,
		posSelector,
		setPosSelector,
		isTopRowVisible,
		handleTopRowVisibilityChange,
		isHomeOrDynamicWithPharmacy,
		isValidCustomer,
		showBrandSectionMenu,
		showBasketButton,
		isHelpPage,
	};
}
